import React, { Suspense, lazy, useState } from 'react'
import { Box } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { type GridSortItem } from '@mui/x-data-grid'
import { RoleManager } from '../../../APIManager'
import LoadingPage from '../../../pages/LoadingPage'
import DeleteUserRole from './DeleteSysteRole/DeleteUserRole'
import AddUserToRoleAction from './AddUserToRoleAction'
import { type ApplyFiltersAndDispatchParams } from '../../../components/CustomTable/types'

const AppTable = lazy(async () => await import('../../../components/CustomTable'))

interface RoleTableProps {
  roleName: string
  roleKey: 'accountOwners' | 'systemManagers'
  roleId?: string
  isLoading?: boolean
}

const RoleTable: React.FC<RoleTableProps> = ({ roleName, roleId, isLoading: parentLoading }) => {
  const [params, setParams] = useState<ApplyFiltersAndDispatchParams>({
    search: '',
    pageSize: 10,
    pageNumber: 1
  })
  const [sortModel, setSortModel] = useState<GridSortItem>()

  const { data: roleData, isLoading } = useQuery({
    queryKey: ['roleData', roleId, params, sortModel],
    enabled: Boolean(roleId),
    queryFn: async () => {
      if (!roleId) return null
      const sort = sortModel && { field: sortModel.field, direction: sortModel.sort }
      const apiParams = {
        search: params?.search || undefined,
        sort: sortModel ? JSON.stringify(sort) : undefined,
        pageSize: params?.pageSize,
        pageNumber: params?.pageNumber,
        scope: params?.scope
      }
      const response = await RoleManager.getRoleData(roleId, apiParams)
      return response.data
    }
  })

  const handlePageChange = async (page: number): Promise<void> => {
    setParams(prev => ({ ...prev, pageNumber: page }))
  }

  const handleRowsPerPageChange = async (pageSize: number): Promise<void> => {
    setParams(prev => ({ ...prev, pageSize, pageNumber: 1 }))
  }

  const onSortModelChange = async (newSortModel: GridSortItem): Promise<void> => {
    setSortModel(newSortModel)
    setParams(prev => ({ ...prev, pageNumber: 1 }))
  }

  const handleSearch = (value: string): void => {
    setParams(prev => ({ ...prev, search: value, pageNumber: 1 }))
  }

  const columns = roleData?.keys ?? []
  const rows = roleData?.data ?? []
  const tableHeader = `${roleName} Management`
  const isTableLoading = isLoading || parentLoading

  return (
    <Box sx={{ width: '100%' }}>
      <Suspense fallback={<LoadingPage />}>
        <AppTable
          topbarTitle={tableHeader}
          list={roleData}
          rows={rows}
          loading={isTableLoading}
          columns={columns}
          featureName={roleName}
          hideEditColumn={true}
          enableAllSelection={false}
          createActionTitle="Add User"
          createAction={<AddUserToRoleAction roleName={roleName} roleId={roleId} handleCloseModal={() => {}} />}
          deleteAction={<DeleteUserRole roleId={roleId || ''} roleName={roleName} />}
          params={params}
          setParams={setParams}
          onSearch={handleSearch}
          handlePageChange={handlePageChange}
          handleRowsPerPageChange={handleRowsPerPageChange}
          onSortModelChange={onSortModelChange}
          showScopeToggle={true}
          scopeToggleTitle={tableHeader}
        />
      </Suspense>
    </Box>
  )
}

export default RoleTable
